import { AdminConsoleRolesApi, AdminConsoleUsersApi } from '@portal/pkg-api/admin-console'
import { EntityTypeApi } from '@portal/pkg-api/navigator'
import { EntitlementApi } from '@portal/pkg-api/security'
import { LicenseType } from '@portal/pkg-api/gen/security'
import { data } from '@portal/pkg-api/helpers'
import { LITE_ROLE_NAME } from './model/builtInRoles'

const prefix = 'admin-console.roles'

export const RoleQueries = {
  list: () => ({
    queryKey: [prefix, 'list'],
    queryFn: () => data(AdminConsoleRolesApi.listRoles()),
  }),
  detail: (id: number) => ({
    queryKey: [prefix, 'detail', id],
    queryFn: () => data(AdminConsoleRolesApi.getRole(id)),
  }),
  scopeSettings: () => ({
    queryKey: [prefix, 'scopeSettings'],
    queryFn: () => data(AdminConsoleUsersApi.getOrgSettings()),
    staleTime: 60_000,
  }),
  entityTypes: () => ({
    queryKey: [prefix, 'entityTypes'],
    queryFn: () => data(EntityTypeApi.getAdminEntityTypes()),
    staleTime: 60_000,
  }),
  /** Who belongs to the role. Includes disabled users, as the legacy screen did. */
  members: (id: number) => ({
    queryKey: [prefix, 'members', id],
    queryFn: () => data(AdminConsoleRolesApi.listRoleMembers(id)),
  }),
  /**
   * Who could be added to it: the organization's users the caller may see, minus the ones
   * already in the role. The server does the subtraction, so this is what the picker offers.
   */
  assignableUsers: (id: number) => ({
    queryKey: [prefix, 'assignableUsers', id],
    queryFn: () => data(AdminConsoleRolesApi.listAssignableUsers(id)),
  }),
}

/**
 * The permissions a role has, and the ones it could have. Two lists that have to be fetched the
 * same way and are matched against each other by `authority`.
 *
 * Both calls carry `licenseType=LITE` for the built-in Lite role and nothing for any other, which
 * is what the legacy screen did. Passing it to one call and not the other would show boxes that
 * could never be ticked.
 *
 * The server does not act on it yet - both read paths in `EntitlementDataserviceImpl` still carry
 * a "TODO compare License Type parameter to entitlements" - so the Lite grid currently offers the
 * same entitlements as any other role. Sending it regardless means the grid narrows on its own
 * the moment that TODO is closed.
 */
export function licenseTypeFor(roleName: string): LicenseType | undefined {
  return roleName === LITE_ROLE_NAME ? LicenseType.LITE : undefined
}

export const EntitlementQueries = {
  available: (roleId: number, roleName: string) => ({
    queryKey: [prefix, 'entitlements', 'available', roleId, licenseTypeFor(roleName)],
    queryFn: () => data(EntitlementApi.getAvailableEntitlements(roleId, licenseTypeFor(roleName))),
  }),
  granted: (roleId: number, roleName: string) => ({
    queryKey: [prefix, 'entitlements', 'granted', roleId, licenseTypeFor(roleName)],
    queryFn: () => data(EntitlementApi.getEntitlements(roleId, licenseTypeFor(roleName))),
  }),
}
